// @flow
import React from 'react';
import { useSelector } from 'react-redux';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Container, Card, CardHeader, CardContent, CardActions } from '@mui/material';
import FormTextField from '../../common/components/FormTextField';
import PasswordTextField from '../../resetPassword/components/PasswordTextField';
import Header from '../../common/components/Header';
import PrimaryButton from '../../common/components/PrimaryButton';
import TertiaryButton from '../../common/components/TertiaryButton';
import ServerErrorAlert from '../../common/components/ServerErrorAlert';
import useNewAccountFormManager from './hooks/useNewAccountFormManager';
import { navigate } from '../../state/navigation/navigation';
import TermsOfUseCheckbox from './TermsOfUseCheckbox';
import LoadingDialog from './LoadingDialog';

type Props = {
    navigateToLogin: () => {},
    onAccountCreate: () => {},
    instructions: string,
    style: string | null
};

export default function CreateAthleteAccount({ navigateToLogin, onAccountCreate, instructions, style }: Props) {
    const isAuthenticated = useSelector((state: any) => state.authentication?.isAuthenticated);

    const navToLogin = navigateToLogin || (() => navigate('/account/login'));
    const createAccountText = 'Create Account';

    const {
        name,
        setName,
        nameValidated,
        nameErrorMessage,
        email,
        setEmail,
        emailValidated,
        emailErrorMessage,
        password,
        setPassword,
        passwordValidated,
        passwordErrorMessage,
        hasAgreedToTerms,
        setHasAgreedToTerms,
        showValidationErrors,
        isInProcess,
        isOpen,
        progress,
        createAccount,
        createAccountSuccessfulOrNotSubmitted,
        serverErrorMessages,
        serverError,
        setServerError,
        HCAPTCHA_SITE_KEY,
        captchaRef,
        captchaRemountKey
    } = useNewAccountFormManager(false);

    if (isAuthenticated && !onAccountCreate) {
        return null;
    }

    const hasNameError = showValidationErrors && !nameValidated;
    const hasEmailError = showValidationErrors && !emailValidated;
    const hasPasswordError = showValidationErrors && !passwordValidated;
    const hasAgreedError = showValidationErrors && !hasAgreedToTerms;
    const nameErrorText = createAccountSuccessfulOrNotSubmitted ? nameErrorMessage : serverErrorMessages.full_name;
    const emailErrorText = createAccountSuccessfulOrNotSubmitted ? emailErrorMessage : serverErrorMessages.email;
    const passwordErrorText = createAccountSuccessfulOrNotSubmitted
        ? passwordErrorMessage
        : serverErrorMessages.password;

    const handleCreateAccountViaEnterKey = event => {
        const enterKeyPressed = event.key === 'Enter' || event.keyCode === 13 || event.which === 13;

        if (enterKeyPressed) {
            createAccount();
        }
    };

    return (
        <div className="outer-container">
            <Header />
            <Container maxWidth="lg" classes={{ root: 'header-space' }}>
                <div className="container payment-container">
                    <Card data-testid="card" classes={{ root: 'form-card card' }}>
                        <CardHeader
                            title="Create Account"
                            classes={{
                                root: 'padding-desktop',
                                title: 'card-title'
                            }}
                        />
                        <form className="form-padding padding-desktop">
                            <div className="form-input">
                                <p className={style || ''}>{instructions}</p>
                            </div>
                            <div className="form-input">
                                <FormTextField
                                    onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                                    required
                                    variant="standard"
                                    type="text"
                                    label="First and Last Name"
                                    value={name}
                                    onChange={event => setName(event.target.value)}
                                    error={hasNameError}
                                    helperText={hasNameError ? nameErrorText : ''}
                                />
                            </div>
                            <div className="form-input">
                                <FormTextField
                                    onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                                    required
                                    variant="standard"
                                    type="email"
                                    label="Email"
                                    value={email}
                                    onChange={event => setEmail(event.target.value)}
                                    error={hasEmailError}
                                    helperText={hasEmailError ? emailErrorText : ''}
                                />
                            </div>
                            <div className="form-input">
                                <PasswordTextField
                                    onKeyDown={event => handleCreateAccountViaEnterKey(event)}
                                    required
                                    label="Password"
                                    value={password}
                                    onChange={value => setPassword(value)}
                                    hasError={hasPasswordError}
                                    helperText={hasPasswordError ? passwordErrorText : ''}
                                />
                            </div>
                        </form>
                        <CardContent classes={{ root: 'padding-desktop' }}>
                            <TermsOfUseCheckbox
                                hasAgreed={hasAgreedToTerms}
                                hasAgreedError={hasAgreedError}
                                onChange={() => setHasAgreedToTerms(!hasAgreedToTerms)}
                            />
                        </CardContent>
                        <CardActions classes={{ root: 'action-container' }}>
                            <HCaptcha
                                key={captchaRemountKey}
                                ref={captchaRef}
                                sitekey={HCAPTCHA_SITE_KEY}
                                size="invisible"
                                onVerify={() => {}}
                            />
                            <br />
                            <br />
                            <div className="action-buttons-container">
                                <PrimaryButton
                                    disabled={isInProcess}
                                    showActivity={isInProcess}
                                    onClick={() => createAccount()}
                                >
                                    {createAccountText}
                                </PrimaryButton>
                            </div>
                            <div className="horizontal-divider">
                                <div className="divider-border" />
                                <div className="divider-text">or</div>
                                <div className="divider-border" />
                            </div>
                            <div className="action-buttons-container button-margin">
                                <TertiaryButton onClick={navToLogin}>Already have an account? Login.</TertiaryButton>
                            </div>
                        </CardActions>
                    </Card>
                </div>
                <LoadingDialog isOpen={isOpen} progress={progress} />
                <ServerErrorAlert open={serverError} handleClose={() => setServerError(false)} />
            </Container>
        </div>
    );
}
